import type { Language } from '../../i18n/translations.js';
import { DEMO_COPY } from './demo-copy.js';
import type { DemoMedia } from './useDemoMedia.js';
import { formatTime } from './useDemoMedia.js';
import { DEMO_EXAMPLES, type DemoExample } from './demo-media.js';
export function RemadeVideoStage({
  example,
  language,
  step,
  go,
  media,
  view,
  setView,
  onRequestAccess,
}: {
  readonly example: DemoExample;
  readonly language: Language;
  readonly step: number;
  readonly go: (next: number) => void;
  readonly media: DemoMedia;
  readonly view: string;
  readonly setView: (view: string) => void;
  readonly onRequestAccess: (from: HTMLElement) => void;
}) {
  const c = DEMO_COPY[language];
  return (
    <section
      className="stage mw-stage mw-result-stage"
      id="stage-4"
      aria-labelledby="result-title"
      hidden={step !== 4}
    >
      <div className="mw-stage-heading">
        <h3 id="result-title" tabIndex={-1}>
          {c.yourRemadeVideo}
        </h3>
        <p>{c.realResultGeneratedInAdvance}</p>
      </div>
      <div className="result-layout">
        <div className="comparison-area">
          <div
            className="mobile-switch"
            role="group"
            aria-label={c.chooseComparisonView}
          >
            <button
              type="button"
              onClick={() => {
                setView('original');
              }}
              aria-pressed={view === 'original'}
            >
              {c.original}
            </button>
            <button
              type="button"
              onClick={() => {
                setView('result');
              }}
              aria-pressed={view === 'result'}
            >
              {c.result}
            </button>
          </div>
          <div className="comparison" data-mobile-view={view}>
            <figure className="compare-original">
              <video
                id="result-before"
                poster={DEMO_EXAMPLES[example].sourcePoster}
                muted
                playsInline
                preload="none"
                aria-label={c.originalVideoBeforeTheSwap}
                ref={media.refs.before}
                onError={media.fail}
                onPlay={media.sync}
                onPause={media.sync}
                onTimeUpdate={media.sync}
                onEnded={media.pause}
              ></video>
              <figcaption>{c.original}</figcaption>
            </figure>
            <figure className="compare-result">
              <video
                id="result-after"
                poster={DEMO_EXAMPLES[example].resultPoster}
                muted
                playsInline
                preload="none"
                aria-label={c.actualPreviouslyGeneratedProductSwapResult}
                ref={media.refs.after}
                onError={media.fail}
                onPlay={media.sync}
                onPause={media.sync}
                onTimeUpdate={media.sync}
                onEnded={media.pause}
              ></video>
              <figcaption>{c.moujaResult}</figcaption>
            </figure>
          </div>
          <div className="timeline">
            <label className="sr" htmlFor="seek">
              {c.videoPosition}
            </label>
            <input
              id="seek"
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={media.percent}
              onChange={(event) => {
                media.seek(Number(event.currentTarget.value));
              }}
              aria-valuetext={formatTime(media.current)}
            />
            <span id="timecode" dir="ltr">
              {formatTime(media.current)} / {formatTime(media.duration)}
            </span>
          </div>
          <div className="comparison-controls">
            <button
              className="btn light"
              id="compare-play"
              type="button"
              onClick={() => void media.toggle()}
              disabled={media.loading}
              aria-pressed={media.playing}
            >
              <span aria-hidden="true">{media.playing ? 'Ⅱ' : '▶'}</span>
              <span>
                {media.loading
                  ? c.loadingLabel
                  : media.playing
                    ? c.pauseComparison
                    : c.playComparison}
              </span>
            </button>
            <button
              className="icon-btn"
              id="compare-replay"
              type="button"
              aria-label={c.replayComparison}
              onClick={() => void media.toggle(true)}
            >
              {'↺'}
            </button>
            <button
              className="text-btn"
              id="compare-sound"
              type="button"
              onClick={media.toggleSound}
              aria-pressed={!media.muted}
            >
              {media.muted ? c.soundOn : c.soundOff}
            </button>
          </div>
        </div>
        <div className="result-aside">
          <div className="result-product" hidden>
            <img src="/landing-demo/cookie.png" alt="" width="88" height="88" />
            <div>
              <span className="summary-label">{c.yourChosenProduct}</span>
              <strong>{c.dropSelected}</strong>
            </div>
          </div>
          <button
            className="btn"
            type="button"
            onClick={(event) => {
              onRequestAccess(event.currentTarget);
            }}
          >
            <span>{c.joinLabel}</span>
            <span className="arrow" aria-hidden="true">
              {'→'}
            </span>
          </button>
          <button
            className="text-btn"
            type="button"
            onClick={() => {
              go(3);
            }}
          >
            {c.changeProduct}
          </button>
          <details className="example-details">
            <summary>{c.aboutThisExample}</summary>
            <p>
              {example === 'croissant'
                ? c.aboutCroissantExample
                : c.theOriginalShowsAPersonCarrying}
            </p>
            <p>{c.thisExampleDemonstratesPhysicalProductReplacement}</p>
          </details>
        </div>
      </div>
    </section>
  );
}
